/**
 * OTRACK-SUBSYSTEM — `TT` ("Temporary Track Point") interactive session.
 *
 * Mirrors `pointFilterSession.ts`'s SHAPE only (not that file itself — kept
 * deliberately SEPARATE and never edits it, per the OTRACK task brief's N10
 * concurrency constraint): a transparent layer over any point prompt that
 * recognises a starter keyword, collects ONE auxiliary point, and hands
 * control back. Unlike FROM/M2P/`.X.Y.Z` — which COMPOSE a resolved point
 * from the auxiliary pick(s) and hand it to the host command — `TT` never
 * produces the command's point at all: the captured point is pushed onto
 * the OTRACK acquired-point store (exactly like a hover-dwell acquisition)
 * and the SAME host prompt re-arms, unchanged, for its real point.
 *
 * Flow: `TT` → point (pick/type, ordinary coordinate grammar — no filter
 * composition applies) → point pushed to `useOtrackAcquisitionStore` →
 * host session resumes at its ORIGINAL step with NO resolved point.
 *
 * Dependency direction: mirrors `pointFilterSession.ts` — imports draft
 * TYPES + shared helpers only, never the state machines that import IT
 * (`draftStateMachine.ts`/`draftModifyStateMachine.ts` call this ADDITIVELY
 * alongside their existing `interceptPointFilterText`/`Point` calls).
 */

import {
  classifyTemporaryTrackPointToken,
  parsePointToken,
} from "@dxf-ai-editor/coordinate-input";

import type { AdvanceResult, DraftPoint, DraftSession, DraftStep } from "./draftTypes";
import {
  draftSessionAcceptsPointPrompt,
  draftStepConsumesFreeTextName,
  referencePointForDraftTextInput,
} from "./draftSharedHelpers";
import { DRAFT_STEP_REGISTRY } from "./drafting/draftStepRegistry";
// Read-only predicate only (never a mutation, never the state machines that
// import both this module AND pointFilterSession.ts — no cycle risk; see
// `Scene3DStage.tsx`'s own established `pointFilterHasCapturedAnchor` /
// `orthoAnchorForDraftSessionWithFilter` imports from the same file for
// precedent). Local Codex review round 5 P2 — "Exclude active point filters
// from dwell acquisition", the FROM/M2P/.X sibling of finding round 4's TT
// exclusion below.
import { isPointFilterActive } from "./pointFilterSession";
import { useOtrackAcquisitionStore } from "../state/otrackAcquisitionStore";

const K_TT_ACTIVE = "otrackTrackPointActive";

/**
 * OTRACK-SUBSYSTEM (local Codex review round 5 P2 — "Exclude entity-pick
 * steps from dwell acquisition") — steps the registry marks
 * `acceptsPointInput: true` (so the DI overlay / ORTHO/POLAR rubber band
 * treat the click as point-ish) whose click actually IDENTIFIES AN EXISTING
 * ENTITY via hit-test (fillet/chamfer edge picks, TRIM/EXTEND/OFFSET single-
 * entity picks, geometric-constraint entity picks, dynamic-block ACTION
 * editor parameter/object picks, MLEADEREDIT leader-removal, REVCLOUD
 * "Object" conversion) rather than requesting a fresh drafting coordinate.
 * Dwelling over a live OSNAP target while choosing WHICH ENTITY to act on
 * must not arm/acquire an OTRACK tracking point — that point has no
 * relationship to the entity being picked and would otherwise leak into a
 * LATER, genuinely coordinate-shaped prompt in the same command (e.g.
 * OFFSET's side-selection click, right after `offset-select-entity`).
 *
 * Deliberately excludes steps whose click supplies a genuine COORDINATE even
 * though the step name also contains "pick"/"select" — e.g.
 * `hatch-pick-internal-point` (any point inside the boundary works — it is
 * the coordinate itself, not an entity id) and `stretch-select-first`/
 * `stretch-select-second` (STRETCH's crossing-window corners are real
 * coordinate points, not an entity hit-test).
 *
 * A maintained, evidence-based allowlist (derived from every registry entry
 * whose `promptKey` reads as "select/pick an EXISTING object" — see the
 * fix commit for the full audit), not a fully re-derived typed registry
 * field — mirrors the SAME documented-scope-boundary precedent
 * `pointFilterSession.ts` already uses for its own registry-vs-drift note.
 * A new entity-pick step added to the registry needs a manual addition here.
 *
 * Local Codex review round 9 P2 ("Exclude ARRAYPATH path-pick from
 * TT/dwell") — re-audited by grepping every `findClosestEntity(point, ...)`
 * call site in `draftModifyStateMachine.ts` (the actual DISCRIMINATING
 * signal — "this step's point argument resolves to an entity via hit-test"
 * — rather than promptKey text alone, which is what missed `arraypath-path`
 * the first time: its promptKey is `prompt.arraypath.path`, containing
 * neither "select" nor "pick"). Cross-checked every `findClosestEntity`
 * call site against the registry's `acceptsPointInput`: every OTHER hit
 * (`move-select`, `copy-select`, `mirror-select`, `rotate-select`,
 * `scale-select`, `arrayrect-select`, `arraypolar-select`,
 * `arraypath-select`, `break-select`, `lengthen-select`) already reads
 * `acceptsPointInput: false` in the registry (a window/crossing selection,
 * not a single click) and is excluded by THAT check already — only
 * `arraypath-path` was a genuine gap.
 */
const ENTITY_PICK_STEPS: ReadonlySet<DraftStep> = new Set<DraftStep>([
  "arrayedit-select",
  "hatchedit-select",
  "dimradius-select-arc",
  "dimdiameter-select-arc",
  "vpclip-select-viewport",
  "offset-select-entity",
  "trim-pick-entity",
  "extend-pick-entity",
  "fillet-first-entity",
  "fillet-second-entity",
  "chamfer-first",
  "chamfer-second",
  "arraypath-path",
  "lengthen-pick-end",
  "geom-constraint-pick-first",
  "geom-constraint-pick-second",
  "geom-constraint-pick-more",
  "geom-constraint-pick-reference-line",
  "mleaderedit-remove-pick",
  "revcloud-object-pick",
  "action-move-pick-parameter",
  "action-move-select-objects",
  "action-scale-pick-parameter",
  "action-scale-select-objects",
  "action-stretch-pick-parameter",
  "action-stretch-select-objects",
  "action-polar-stretch-pick-parameter",
  "action-polar-stretch-select-objects",
  "action-rotation-pick-parameter",
  "action-rotation-select-objects",
  "action-flip-pick-parameter",
  "action-flip-select-objects",
  "action-array-pick-parameter",
  "action-array-select-objects",
  "action-lookup-pick-parameter",
  "action-lookup-select-objects",
]);

/** True while a `TT` sub-prompt is mid-collection. */
export function isTemporaryTrackPointActive(session: DraftSession): boolean {
  return session.options[K_TT_ACTIVE] === "1";
}

/**
 * OTRACK-SUBSYSTEM — hover-dwell acquisition gate (local Codex review round 4
 * P2, "Exclude active TT prompts from dwell acquisition"). Combines
 * `draftSharedHelpers.draftSessionAcceptsPointPrompt`'s registry-driven
 * classification with an ADDITIONAL exclusion for an active `TT` sub-prompt.
 *
 * A `TT` sub-prompt deliberately leaves `session.step` UNCHANGED (still the
 * host command's point step — see this module's top doc), so the registry
 * signal alone reads it as an ordinary point prompt and the hover-dwell timer
 * would stay armed for its duration. Left unguarded, a dwell-then-click TT
 * interaction (hover over an OSNAP target long enough to arm, then click it
 * to complete TT) double-toggles the SAME acquired point: the dwell timer
 * acquires it first, then TT's own click handler
 * ({@link interceptTemporaryTrackPointPoint}) immediately un-acquires it —
 * silently leaving no temporary tracking point at all.
 *
 * The single call site (`Scene3DStage.tsx`'s `draftPointPromptActiveRef`
 * wiring) uses THIS function rather than composing the two checks inline, so
 * the combined predicate has one definition and one focused test suite. Lives
 * here (not in `draftSharedHelpers.ts`) because this module already imports
 * FROM `draftSharedHelpers.ts` — the reverse import would be a cycle.
 *
 * Local Codex review round 5 P2 ("Exclude active point filters from dwell
 * acquisition") — extends the SAME exclusion to an active FROM/M2P/`.X` point
 * filter: like `TT`, a point filter is a transparent sub-prompt that leaves
 * `session.step` unchanged (the host command's own point step), so the
 * registry-driven signal alone cannot tell "the user is choosing the
 * filter's auxiliary point" apart from "the user is choosing the host
 * command's real point". Left unguarded, dwelling over an OSNAP target while
 * (say) picking FROM's base point would arm/acquire an OTRACK point that
 * then leaks into the HOST command's later real point prompt in the same
 * command.
 *
 * Local Codex review round 5 P2 ("Exclude entity-pick steps from dwell
 * acquisition") — also excludes {@link ENTITY_PICK_STEPS}: those steps
 * genuinely accept a point-shaped click per the registry, but the click
 * identifies an EXISTING entity, not a fresh coordinate — see that
 * constant's doc.
 */
export function draftSessionAcceptsDwellAcquisition(
  session: DraftSession | null,
): boolean {
  return (
    draftSessionAcceptsPointPrompt(session) &&
    !(session != null && isTemporaryTrackPointActive(session)) &&
    !(session != null && isPointFilterActive(session)) &&
    !(session != null && ENTITY_PICK_STEPS.has(session.step))
  );
}

function beginTemporaryTrackPoint(session: DraftSession): DraftSession {
  return {
    ...session,
    options: { ...session.options, [K_TT_ACTIVE]: "1" },
  };
}

/**
 * Cancel/complete an ACTIVE `TT` sub-prompt, returning to the host session
 * unchanged (same `step`/`points`) — exported for the canvas Escape handler,
 * mirroring `pointFilterSession.clearPointFilter`'s role for FROM/M2P.
 */
export function clearTemporaryTrackPoint(session: DraftSession): DraftSession {
  const options = { ...session.options };
  delete options[K_TT_ACTIVE];
  return { ...session, options };
}

function acquireAndResume(session: DraftSession, point: DraftPoint): AdvanceResult {
  // Local Codex review round 5 P2 — the acquired point is captured in the
  // HOST session's own space (`TT` applies no filter/space composition of
  // its own), matching every other TT-collected coordinate.
  //
  // DRAFT-DOCUMENT-FRAME — and in the store's own frame, which is the
  // DOCUMENT one, so `point` goes in VERBATIM.
  //
  // `useOtrackAcquisitionStore` holds DOCUMENT coordinates precisely because
  // its two writers sit on opposite sides of the pointer pipeline. The other
  // one — the hover-dwell acquisition in `pickLoop/interactivePickLoop.ts` —
  // holds an ACTIVE-SPACE snap point and converts with that space's own
  // `ActiveInputSpace.toDocument`; `point` here arrives from the draft state
  // machine, which already works in DOCUMENT coordinates, so it needs no
  // conversion at all. The readers convert back per sample with
  // `.fromDocument` (`otrackDraftLock`'s alignment, the pick loop's "+"
  // markers).
  //
  // This module could NOT do it any other way: the active space is not
  // frame-uniform (identity ⇒ offset-world, MSPACE_VIA_VP over an unlocked
  // VP ⇒ document) and `session.spaceId` is `"model"` in BOTH of those, so a
  // draft-machine module with no pointer context cannot tell the branches
  // apart. Subtracting the session origin here unconditionally — as this did
  // — stored a `TT` point acquired through a floating viewport a whole origin
  // away from where it was picked.
  useOtrackAcquisitionStore
    .getState()
    .toggleAcquirePoint(point, session.spaceId);
  return { nextSession: clearTemporaryTrackPoint(session) };
}

/**
 * Interception shim for a text-input dispatcher — mirrors
 * `pointFilterSession.interceptPointFilterText`'s external contract, but
 * with NO `redispatchPoint` callback: `TT` never resolves the host
 * command's point, so there is nothing to redispatch. Returns an
 * `AdvanceResult` when this layer handled the input, or `null` to fall
 * through to the caller's normal text handling (including the EXISTING
 * point-filter interceptor for FROM/M2P/`.X.Y.Z`, which this call sits
 * ahead of at each call site).
 */
export function interceptTemporaryTrackPointText(
  session: DraftSession,
  text: string,
): AdvanceResult | null {
  if (!isTemporaryTrackPointActive(session)) {
    // Only a genuine POINT prompt may START a TT flow — mirrors the guard
    // `pointFilterSession.interceptPointFilterText` applies for the same
    // reason (a free-form NAME step, e.g. block-name entry, must let a
    // literal "tt" through as text, not swallow it as a keyword). Local
    // Codex review round 8 P2 ("Restrict TT startup to coordinate
    // prompts") — ALSO excludes {@link ENTITY_PICK_STEPS}, the SAME set
    // `draftSessionAcceptsDwellAcquisition` excludes from hover-dwell: the
    // registry marks these `acceptsPointInput: true` because their click is
    // used for hit-testing, but the click identifies an EXISTING entity,
    // not a coordinate. Without this, typing `TT` at (e.g.) OFFSET's
    // "select object" prompt would start a TT flow, and the user's very
    // next click — meant to SELECT the object — would instead be consumed
    // as TT's acquisition point, leaking a stale un-selected state into the
    // following side/point prompt.
    //
    // Local Codex review round 10 P2 ("Block TT startup while point
    // filters are active") — ALSO excludes an active FROM/M2P/`.X` point
    // filter, mirroring `draftSessionAcceptsDwellAcquisition`'s SAME
    // exclusion (round 5 P2) for the identical reason: `session.step`
    // stays the HOST point step while a filter is mid-collection, so this
    // check alone cannot tell "the filter's own auxiliary point prompt"
    // apart from "the host's real point prompt". Both interceptors are
    // wired ahead of `interceptPointFilterText` at every call site (this
    // module's own top doc), so without this exclusion typing `TT` while
    // FROM is collecting its base point would hijack the token as TT's OWN
    // starter — the active filter never sees it, and the user's next pick
    // is consumed as an OTRACK acquisition instead of the filter's
    // auxiliary point.
    if (
      !DRAFT_STEP_REGISTRY[session.step].acceptsPointInput ||
      draftStepConsumesFreeTextName(session.step) ||
      ENTITY_PICK_STEPS.has(session.step) ||
      isPointFilterActive(session)
    ) {
      return null;
    }
    if (!classifyTemporaryTrackPointToken(text)) return null;
    return { nextSession: beginTemporaryTrackPoint(session) };
  }

  // An empty Enter / Escape cancels the TT sub-prompt (AutoCAD parity,
  // mirrors the point-filter Escape handling).
  if (text.trim() === "" || text === "\x1b" || text.toUpperCase() === "ESC") {
    return { nextSession: clearTemporaryTrackPoint(session) };
  }

  // Ordinary coordinate grammar — TT applies NO filter composition of its
  // own (unlike FROM's base+offset or M2P's midpoint); the typed token
  // resolves against the HOST session's own reference/hover, exactly as if
  // the user had typed it directly at the point prompt.
  const reference = referencePointForDraftTextInput(session);
  const parsed = parsePointToken(text, {
    referencePoint: reference ? { ...reference, z: 0 } : null,
    directionPoint: session.hoverPoint ? { ...session.hoverPoint, z: 0 } : null,
  });
  if (!parsed.ok) {
    // Keep collecting — an unparseable token leaves the TT flow armed.
    return { nextSession: session };
  }
  return acquireAndResume(session, { x: parsed.value.x, y: parsed.value.y });
}

/**
 * Interception shim for a point-input dispatcher (a mouse pick). Mirrors
 * {@link interceptTemporaryTrackPointText} for picked points — a pick never
 * STARTS a TT flow (the starter is the typed `TT` keyword), so an inactive
 * session is not handled here.
 */
export function interceptTemporaryTrackPointPoint(
  session: DraftSession,
  point: DraftPoint,
): AdvanceResult | null {
  if (!isTemporaryTrackPointActive(session)) return null;
  return acquireAndResume(session, point);
}
